import RainbowText from '../atoms/RainbowText';
import { colors } from '../../utils/styling';

// The "why" section McKayla asked for in her 2026-10-05 design Loom ("this is
// the why and I think that's also something that's missing right now"). Copy is
// verbatim from her Canva "Money Personality Website Design - Desktop", page 11.
// Original Loom + frames: HP3-Files/Clients/Nina Hajjar/Email Evidence/2026-10-05/.

const font = 'Nunito';

const CONVERSATIONS = [
  {
    title: 'With Your Partner',
    bg: '#dcd0f5',
    body:
      'Take the quiz separately, then compare your results. Use it as a launching point to talk about spending styles, money values, and shared goals — building alignment and understanding in your financial relationship.',
  },
  {
    title: 'With Your Kids',
    bg: '#a8dcc6',
    body:
      'Discover how your child naturally thinks about money and identify areas where you can guide them toward smart financial habits. Great for raising financially literate kids and future decision-makers.',
  },
  {
    title: 'Across Generations',
    bg: '#a9e0ec',
    body:
      'Open the door to meaningful family discussions about values, legacy, and wealth transfer. Use the quiz to help frame conversations with aging parents or adult children around what wealth means to your family.',
  },
];

const Heading = ({ bold, rest, boldFirst = true }: { bold: string; rest: string; boldFirst?: boolean }) => (
  <h2 style={{ fontFamily: font, fontWeight: 400, fontSize: 'clamp(26px, 3.4vw, 40px)', margin: 0, textAlign: 'center', color: colors.black }}>
    {boldFirst ? (
      <>
        <b>{bold}</b> {rest}
      </>
    ) : (
      <>
        {rest} <b>{bold}</b>
      </>
    )}
  </h2>
);

const WhySection = ({ isMobile }: { isMobile: boolean }) => (
  <section style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    {/* Intro + animated arrow */}
    <div className="mp-reveal" style={{ textAlign: 'center', padding: isMobile ? '40px 20px 8px' : '72px 40px 16px' }}>
      <div style={{ fontFamily: font, fontSize: 'clamp(24px, 3vw, 38px)', fontWeight: 300 }}>
        The Multi-Purpose <b style={{ fontWeight: 800 }}>Benefits</b> of Your Money
      </div>
      <RainbowText text="personality" />
      <div className="mp-bounce-arrow" aria-hidden>
        <svg width="44" height="28" viewBox="0 0 44 28">
          <polyline points="4,6 22,22 40,6" fill="none" stroke="#2bb38a" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>

    {/* Creates Self-Awareness */}
    <div style={{ width: '100%', background: '#fdfbe6', padding: isMobile ? '40px 20px' : '72px 40px', boxSizing: 'border-box' }}>
      <div className="mp-reveal" style={{ maxWidth: 860, margin: '0 auto', textAlign: 'center', fontFamily: font }}>
        <h2 style={{ fontWeight: 400, fontSize: 'clamp(28px, 3.6vw, 44px)', margin: '0 0 20px' }}>
          Creates <b>Self-Awareness</b>
        </h2>
        <p style={{ fontSize: isMobile ? 17 : 20, lineHeight: 1.6, margin: '0 0 20px', color: colors.gray[800] }}>
          Since childhood, our behaviors around money have been influenced by our environment, family, and experiences.
          Our quiz helps bring those patterns to light.
        </p>
        <ul style={{ display: 'inline-block', textAlign: 'left', fontSize: isMobile ? 18 : 21, lineHeight: 1.7, margin: '0 0 24px', paddingLeft: 24 }}>
          <li>Understand Yourself Better</li>
          <li>Recognize your Emotional Influences</li>
          <li>Mitigate Irrational Decisions</li>
        </ul>
        <p style={{ fontSize: isMobile ? 18 : 22, lineHeight: 1.5, margin: 0 }}>
          Knowing yourself is the first step to becoming a more <b>confident</b> saver, <b>intentional</b> spender, and{' '}
          <b>well-rounded</b> investor.
        </p>
      </div>
    </div>

    {/* Strengthen Financial Conversations */}
    <div style={{ width: '100%', maxWidth: 1240, padding: isMobile ? '48px 20px' : '80px 40px', boxSizing: 'border-box' }}>
      <div className="mp-reveal">
        <Heading bold="Strengthen" rest="Financial Conversations" />
      </div>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 28, marginTop: 40 }}>
        {CONVERSATIONS.map((c, i) => (
          <div key={c.title} className="mp-reveal" style={{ flex: 1, transitionDelay: `${i * 120}ms` }}>
            <div style={{ fontFamily: font, fontWeight: 800, fontSize: 20, textAlign: 'center', marginBottom: 12 }}>{c.title}</div>
            <div
              className="mp-lift"
              style={{ background: c.bg, borderRadius: 24, padding: '28px 26px', fontFamily: font, fontSize: 17, lineHeight: 1.6, textAlign: 'center', minHeight: isMobile ? undefined : 250 }}
            >
              {c.body}
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* A Strategic Tool for Your Financial Professionals */}
    <div style={{ width: '100%', background: '#d9f3ea', padding: '28px 20px', boxSizing: 'border-box' }}>
      <Heading bold="Strategic Tool" rest="A" boldFirst={false} />
      <div style={{ textAlign: 'center', fontFamily: font, fontSize: 'clamp(22px, 2.6vw, 34px)', marginTop: 4 }}>for Your Financial Professionals</div>
    </div>
    <div
      style={{
        width: '100%',
        maxWidth: 1100,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isMobile ? 28 : 56,
        padding: isMobile ? '40px 20px' : '64px 40px',
        boxSizing: 'border-box',
      }}
    >
      <div
        className="mp-reveal mp-lift"
        style={{ flex: 1, maxWidth: 560, border: '3px solid #f2b6d4', borderRadius: 28, padding: isMobile ? 24 : 40, background: '#fff', textAlign: 'center', fontFamily: font }}
      >
        <div style={{ fontSize: isMobile ? 22 : 26, marginBottom: 16 }}>
          Help Your Advisor, CPA, and Attorney <b><i>Understand</i></b> You
        </div>
        <p style={{ fontSize: 17, lineHeight: 1.6, margin: 0 }}>
          Your financial team works best when they understand how you approach money. By sharing your quiz results, they gain
          insight into your natural tendencies, risk tolerance, and behavioral patterns — so they can make recommendations that
          actually fit.
        </p>
      </div>
      <div
        className="mp-reveal"
        style={{
          width: isMobile ? 240 : 270,
          height: isMobile ? 240 : 270,
          borderRadius: '50%',
          border: '4px solid #2bb38a',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 32,
          boxSizing: 'border-box',
          textAlign: 'center',
          fontFamily: font,
          background: '#fff',
          transitionDelay: '150ms',
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 22, marginBottom: 8 }}>💡 TIP</div>
        <div style={{ fontSize: 16, lineHeight: 1.5 }}>
          Print or download your quiz results and bring them to your next financial planning or estate strategy meeting.
        </div>
      </div>
    </div>

    {/* Enhance Your AI Experience */}
    <div style={{ width: '100%', background: 'linear-gradient(120deg, #eef6ff 0%, #f5edff 50%, #e9fbf4 100%)', padding: isMobile ? '48px 20px' : '80px 40px', boxSizing: 'border-box' }}>
      <div className="mp-reveal" style={{ maxWidth: 820, margin: '0 auto', textAlign: 'center', fontFamily: font }}>
        <h2 style={{ fontWeight: 400, fontSize: 'clamp(28px, 3.4vw, 42px)', margin: '0 0 20px' }}>
          <b>Enhance</b> Your AI Experience
        </h2>
        <p style={{ fontSize: isMobile ? 17 : 20, lineHeight: 1.6, margin: 0 }}>
          If you're using AI-powered tools for budgeting, investing, or financial advice, this quiz can help your AI serve you even
          better. Upload your results so your AI understands your values, behaviors, and preferences.
        </p>
      </div>
    </div>
  </section>
);

export default WhySection;
